import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { BottomSheet, Icon, orange, PlainButton, tokens } from 'components/v3';
import { useBuilder } from '../BuilderContext';
import { fieldName } from '../desktop/FieldCard';
import { PaletteBody, SavedFieldPicker } from '../desktop/Palette';
import {
  addFieldAction,
  addSavedFieldAction,
  insertionIndex,
  slotBounds,
} from '../state/builderReducer';

// "Add field" (plan §5.7): a bottom sheet on phones, a centred dialog on
// desktop ("+ Add field" at the end of the sheet). It says where the field
// will go — after the selected field or the chosen one, or at the end —
// with a Change link that switches the target to the end. Then search, the
// account-info card and the field groups, with "Add a saved field" pinned
// to the bottom of the sheet.

const ChangeButton = styled(PlainButton)`
  padding: 6px 4px;
  font-size: 13px;
  font-weight: 700;
  color: ${orange(1)};
`;

// → { text, canChange }. With locked fields (w9-plan §6.5) the field lands
// in the payee slot, so "the end" is the slot's end and Change moves it
// there.
export const insertNote = (fields, target, selectedId, lockedFieldIds) => {
  if (!fields.length)
    return { text: 'Adding the first field', canChange: false };
  const at = insertionIndex(fields, target, selectedId, lockedFieldIds);
  const { end } = slotBounds(fields, lockedFieldIds);
  if (at >= fields.length)
    return { text: 'Adding at the end', canChange: false };
  if (at === 0)
    return { text: 'Adding at the top of the form', canChange: true };
  return {
    text: `Adding after “${fieldName(fields[at - 1])}”`,
    canChange: at !== end,
  };
};

const AddFieldSheet = ({ open, onClose, mobile }) => {
  const t = tokens(useTheme());
  const { state, dispatch, lockedFieldIds } = useBuilder();
  const note = insertNote(
    state.working.fields,
    state.insertTarget,
    state.selectedId,
    lockedFieldIds
  );

  const add = (fieldType) => {
    dispatch(addFieldAction(fieldType, { mobile }));
    onClose();
  };
  const addSaved = (saved) => {
    dispatch(addSavedFieldAction(saved, { mobile }));
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Add field"
      maxHeight="85vh"
      footer={
        <div style={{ padding: '0 4px' }}>
          <SavedFieldPicker onAddSaved={addSaved} />
        </div>
      }
    >
      <div
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '0 4px 14px',
          padding: '8px 12px',
          borderRadius: 8,
          background: orange(0.08),
          border: '1px solid ' + orange(0.28),
          fontSize: 13,
          color: t.sub,
        }}
      >
        <Icon name="plus" size={14} color={orange(1)} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {note.text}
        </span>
        {note.canChange ? (
          <ChangeButton
            aria-label="Add at the end instead"
            onClick={() =>
              dispatch({ type: 'SET_INSERT_TARGET', target: { mode: 'end' } })
            }
          >
            Change
          </ChangeButton>
        ) : null}
      </div>
      <div style={{ padding: '0 4px 8px' }}>
        <PaletteBody onAddType={add} tall={mobile} />
      </div>
    </BottomSheet>
  );
};

AddFieldSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mobile: PropTypes.bool,
};

AddFieldSheet.defaultProps = { mobile: false };

export default AddFieldSheet;
